import { URLParams, createUrl, nameMatch, request } from 'app/utils';
import { Col, Flex, Loading, Page, RichContent, Row, Section2, Typography } from 'components';
import { ExamsCard, ExamsListResponse, useExams } from 'features/exams';
import { factoryCardSearch } from 'features/exams/components/examsCard';
import { LivesApiResponse, LivesCard, useLives } from 'features/lives';
// import { fakeRecurrences } from 'features/offers/recurrences';
import { AvailableClassesApiResponse, ProductDetailsApiResponse, ProductEnrollmentBox, ProductsCard, useProducts } from 'features/products';
import { useSchool } from 'features/school';
// import { TeachersApiResponse, TeachersCard, TeachersList, useTeachers } from 'features/teachers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LogoMelver, LogoInvestSmart, LogoLiberta, LogoSafra, LogoXP, ProgramWrapper } from './styles';
import { useMenu } from 'features/menu';

interface DetailsProps {
  enroll?: boolean;
}

const Details = ({ enroll = false }: DetailsProps) => {
  const [loading, setLoading] = useState(true);
  const [paymentLink, setPaymentLink] = useState<string>('');

  const { productSlug, productClassUuid } = useParams();
  const navigate = useNavigate();

  const { apiGatewayUrl } = useSchool();
  const { productDetails, setProductDetails, clearProductDetails, availableClasses, setAvailableClasses, clearAvailableClasses } = useProducts();
  const { lives, setLives, clearLives } = useLives();
  const { exams, setExams, clearExams } = useExams();
  const { setBackPathname } = useMenu();
  // const { teachers, setTeachers, clearTeachers } = useTeachers();

  const getProductClassUuid = useCallback(
    (receivedProductClassUuid: string = '') => {
      return productClassUuid || receivedProductClassUuid || availableClasses?.current?.productClassUuid;
    },
    [availableClasses, productClassUuid]
  );

  const [searchParams] = useSearchParams();
  const { schoolAlias } = useSchool();
  const src = searchParams.get('src') || '';

  const apiFetch = useCallback(
    async (forceEnroll = false) => {
      const params = URLParams({
        enroll: forceEnroll || enroll,
        sendEmail: (forceEnroll || enroll) && (schoolAlias !== 'b3'),
        src,
      });

      await request<ProductDetailsApiResponse>({
        url: `/api/v1/School/Products/${productSlug}${getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}&${params}` : `?${params}`}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      })
        .then(async ({ data }) => {
          setProductDetails(data);

          await Promise.all([
            request<ExamsListResponse>({
              url: `/api/v1/School/ProductContentPracticeExams/${productSlug}${
                getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``
              }`,
              method: 'get',
              baseURL: apiGatewayUrl,
            }).then(({ data }) => {
              setExams(data);
            }),

            request<LivesApiResponse>({
              url: `/api/v1/School/ProductContentLives/${productSlug}${getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``}`,
              method: 'get',
              baseURL: apiGatewayUrl,
            }).then(({ data }) => {
              setLives(data);
            }),

            // request<TeachersApiResponse>({
            //   url: `/api/v1/School/ProductContentTeachers/${productSlug}${getProductClassUuid() ? `?productClassUuid=${getProductClassUuid()}` : ``}`,
            //   method: 'get',
            //   baseURL: apiGatewayUrl,
            // }).then((response) => {
            //   setTeachers(response.data);
            // }),

            request<AvailableClassesApiResponse>({
              url: `/api/v1/School/AvailableClasses/${productSlug}`,
              method: 'get',
              baseURL: apiGatewayUrl,
            }).then(({ data }) => {
              setAvailableClasses(data);

              if (data?.current?.allowEnrollments) {
                request<{
                  link: string;
                }>({
                  url: `/api/v1/OfferPayment/Buy/${getProductClassUuid(data?.current?.productClassUuid)}`,
                  method: 'get',
                  baseURL: apiGatewayUrl,
                }).then(({ data }) => {
                  setPaymentLink(data.link);
                });
              }
            }),
          ]).finally(() => {
            setLoading(false);
          });
        })
        .catch(() => {
          navigate(createUrl('dashboard'));
        });
    },
    [productSlug, apiGatewayUrl, getProductClassUuid(), src]
  );

  useEffect(() => {
    apiFetch();

    return () => {
      setBackPathname(createUrl('programDetails', [productSlug]));
    };
  }, [productSlug, src]);

  useEffect(() => {
    return () => {
      clearProductDetails();
      clearAvailableClasses();
      clearLives();
      clearExams();
    };
  }, []);

  const hasLogoXP = useMemo(() => {
    const isXP = nameMatch(productDetails?.product?.name, ['formação', 'xp']);

    return productSlug.search('xp') !== -1 || isXP;
  }, [productSlug, productDetails]);

  if (loading) return <Loading label="Por favor, aguarde." />;

  //   const isRecurrenceFake = fakeRecurrences.includes(paymentLink.split('/')?.[3]);

  return (
    <Page title={productDetails?.product?.name}>
      <ProgramWrapper background={`url(${productDetails?.product?.coverImageUrl})`}>
        <Section2 flexDirection="column" pb={100}>
          {productSlug.search('cnpi') !== -1 && (
            <Flex mt={{ m: 30, t: 50, d: 100 }} mb={{ m: 30, t: 50, d: 160 }}>
              <LogoMelver src="https://files.melver.com.br/file/melver/Logo_Melver2x.png" />
            </Flex>
          )}

          {hasLogoXP && (
            <Flex mt={{ m: 30, t: 50, d: 100 }} mb={{ m: 30, t: 50, d: 160 }}>
              <LogoXP src="/images/program-xp-logo.png" />
            </Flex>
          )}

          {productSlug.search('Investsmart') !== -1 && (
            <Flex mt={{ m: 30, t: 50, d: 100 }} mb={{ m: 30, t: 50, d: 160 }}>
              <LogoInvestSmart src="/images/invest-smart-logo.png" />
            </Flex>
          )}

          {productSlug.search('plano-formacao-assessores-safra') !== -1 && (
            <Flex mt={{ m: 30, t: 50, d: 100 }} mb={{ m: 30, t: 50, d: 160 }}>
              <LogoSafra src="/images/safra-logo.png" />
            </Flex>
          )}

          {productSlug.search('plano-formacao-assessores-liberta') !== -1 && (
            <Flex mt={{ m: 30, t: 50, d: 100 }} mb={{ m: 30, t: 50, d: 160 }}>
              <LogoLiberta src="/images/liberta-logo.png" />
            </Flex>
          )}

          {productSlug.search('Especializacao-em-planejamento-patrimonial') !== -1 && (
            <Flex mt={{ m: 30, t: 50, d: 100 }} mb={{ m: 30, t: 50, d: 160 }}>
              <LogoLiberta src="/images/mag-logo.png" />
            </Flex>
          )}

          <Flex flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between" flexWrap="nowrap" mt={{ m: 40, t: 50, d: 100 }}>
            <Flex flexDirection="column" mb={{ m: 5, d: 0 }}>
              <Typography color="gray_500" upper mb={1}>
                {productDetails?.product?.hat}
              </Typography>

              <Typography type="type8" color="white" fontWeight="bold" mb={{ m: 3, t: 4 }}>
                {productDetails?.product?.name}
              </Typography>

              <RichContent defaultType="type2" currentTheme="dark">
                {productDetails?.course?.longHeaderDescription}
              </RichContent>
            </Flex>

            {!!productDetails && (
              <>
                {!productDetails?.enrolled && (
                  <>
                    <Flex maxWidth={{ d: 450 }} flexShrink={0} ml={{ d: 50 }}>
                      <ProductEnrollmentBox productType={productDetails?.course?.productType} paymentLinkUrl={paymentLink} apiFetch={apiFetch} />
                      {/* {schoolAlias === 'b3' ? (
                        <>
                          {availableClasses?.current?.allowEnrollments && productDetails?.allowOnDemandEnrollments ? (
                            <ProductsSidebarButton onClick={() => apiFetch(true)} label="Matricular agora" type="nolink" to="" borderRadiusTop />
                          ) : (
                            <ProductsSidebarInterestButton label={`Tenho interesse nesse programa`} labelMobile="Tenho interesse" bordered />
                          )}
                        </>
                      ) : (
                        <>
                          {availableClasses?.current?.allowEnrollments && !!paymentLink ? (
                            <Flex flexDirection="column">
                              <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                                <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                                  {productDetails?.product.hat}
                                </Typography>

                                <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                                  {productDetails?.product.name}
                                </Typography>

                                <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                                  {productDetails?.product.description}
                                </Typography>
                              </Flex>

                              {availableClasses.current.installmentValue ? (
                                <Flex flexDirection="column">
                                  <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                                    <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                                      Compre
                                    </Typography>

                                    <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2}>
                                      {availableClasses.current.installment}x {brazilianRealFormat(availableClasses.current.installmentValue)}
                                    </Typography>

                                    {!isRecurrenceFake && (
                                      <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                                        ou {brazilianRealFormat(availableClasses.current.price)} a vista
                                      </Typography>
                                    )}
                                  </Flex>

                                  <ProductsSidebarButton label="Comprar agora" type="external" to={paymentLink} target="_blank" />
                                </Flex>
                              ) : (
                                <Flex flexDirection="column">
                                  <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                                    <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                                      Curso
                                    </Typography>

                                    <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                                      Gratuito
                                    </Typography>
                                  </Flex>

                                  <ProductsSidebarButton label="Matricular agora" type="external" to={paymentLink} target="_blank" />
                                </Flex>
                              )}
                            </Flex>
                          ) : (
                            <Flex flexDirection="column">
                              <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                                <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                                  {productDetails?.product.hat}
                                </Typography>

                                <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                                  {productDetails?.product.name}
                                </Typography>

                                <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                                  {productDetails?.product.description}
                                </Typography>
                              </Flex>

                              <Flex flexDirection="column">
                                <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                                  <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                                    Inscrição
                                  </Typography>

                                  <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                                    Matrícula não disponível
                                  </Typography>

                                  <Typography as="span" type="type2" lineHeight="1.3" color="white" mt={2}>
                                    Demonstre seu interesse nesse programa.
                                  </Typography>
                                </Flex>

                                <ProductsSidebarInterestButton label={`Tenho interesse nesse programa`} />
                              </Flex>
                            </Flex>
                          )}
                        </>
                      )} */}
                    </Flex>
                  </>
                )}
              </>
            )}
          </Flex>

          <Row gutter={20} mt={{ m: 5, t: 4, d: 7 }}>
            {productDetails?.productsInsideProgram?.courses?.map((product, index) => (
              <Col justifyContent={{ m: 'center', t: 'center' }} width={{ m: 1, t: 1 / 2, d: 1 / 4 }} pb={20} key={`product-${index.toString()}`}>
                <ProductsCard product={product} theme="dark" />
              </Col>
            ))}
          </Row>

          {!!exams?.length && (
            <Flex flexDirection="column" mt={{ m: 5, t: 6, d: 8 }}>
              <Typography type="type8" color="white" fontWeight="bold">
                Avaliações
              </Typography>

              <Row gutter={20} mt={4}>
                {exams.map((exam, index) => (
                  <Col justifyContent={{ m: 'center', t: 'center' }} width={{ m: 1, t: 1 / 2, d: 1 / 4 }} pb={20} key={`exam-${index.toString()}`}>
                    <ExamsCard {...factoryCardSearch(exam)} />
                  </Col>
                ))}
              </Row>
            </Flex>
          )}

          {!!lives?.length && (
            <Flex flexDirection="column" mt={{ m: 5, t: 6, d: 8 }}>
              <Typography type="type8" color="white" fontWeight="bold">
                Encontros ao vivo
              </Typography>

              <Row gutter={20} mt={4}>
                {lives.map((live, index) => (
                  <Col justifyContent={{ m: 'center', t: 'center' }} width={{ m: 1, t: 1 / 2, d: 1 / 4 }} pb={20} key={`live-${index.toString()}`}>
                    <LivesCard live={live} productClassUuid={live.productClassUuid || ''} enrolled={productDetails?.enrolled} />
                  </Col>
                ))}
              </Row>
            </Flex>
          )}

          {/* {!!teachers?.length && (
            <Flex flexDirection="column" mt={{ m: 5, t: 6, d: 8 }}>
              <Typography type="type8" color="white" fontWeight="bold">
                Encontros ao vivo
              </Typography>

              <Row gutter={20} mt={4}>
                {teachers.map((teacher, index) => (
                  <Col justifyContent={{ m: 'center', t: 'center' }} width={{ m: 1, t: 1 / 2, d: 1 / 4 }} pb={20} key={`live-${index.toString()}`}>
                    <TeachersCard teacher={teacher} />
                  </Col>
                ))}
              </Row>
            </Flex>
          )} */}

          {!!productDetails?.course?.longTrailerDescription && (
            <Flex flexDirection="column" mt={{ m: 6, t: 8, d: 10 }}>
              <RichContent defaultType="type2" currentTheme="dark">
                {productDetails?.course?.longTrailerDescription}
              </RichContent>
            </Flex>
          )}
        </Section2>
      </ProgramWrapper>
    </Page>
  );
};

export default Details;
